import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Input from "../Input/Input";
import barMarker from "../../assets/map/bar-marker.png";
import { backendUrl } from "../../api/constants";
import axiosInstance from "../../api/axiosInstance";
import { getVenues } from "../../api/events";
import { styles } from "../PortalStyles/portalStyles.style";
import ImageSlider from "../ImageSlider/ImageSlider";
import { useLocation } from "react-router-dom";
import { ErrorMessage } from "../Input/InputError";
import { DateTime } from "luxon";
import Tabs, { TabContent } from "./tabs";
import { RRule } from "rrule";
import Radio from "../Radio/Radio";
import Logo from "../../assets/imgs/inyc.svg";
import { FaChevronDown, FaPlus } from "react-icons/fa6";
import "./EditEvent.css";
import { useData } from "../../context/DataContext/DataProvider";
import SocialsHandler from "../SocialsInput/SocialsHandler";
import TagInput from "../TagInput/TagInput";
import ArtistSelector from "../ArtistSelector/ArtistSelector";
import { PortalTitle } from "../../pages/Portal";
import { IoCloseOutline, IoLocationSharp } from "react-icons/io5";
import KitButton from "../kit/Button";
import IconButton from "../kit/IconButton";
import Chip from "../kit/Chip";
import OptionCard from "../kit/OptionCard";
import Dropdown from "../kit/Dropdown";
import { errorSummary } from "../../util/formErrors";
import { TbTicket } from "react-icons/tb";
import { FcGoogle } from "react-icons/fc";
import AddressHandler from "../AddressHandler/AddressHandler";
import { ImHome } from "react-icons/im";
import { Popup } from "react-leaflet";
import { RecurrenceSelector } from "../RecurrenceSelector";
import { LocationSelector } from "../LocationSelector";
import { formatAddressComponents, parseAddressString } from "lib";
import { normalizeTicketsForBackend } from "../../util/ticketUtils";

const frequencyMapping = {
  month: RRule.MONTHLY,
  week: RRule.WEEKLY,
  day: RRule.DAILY,
};

const getCoordinatesFromGeoJson = (location) => {
  if (!location?.coordinates) return null;

  return {
    lat: location.coordinates[1],
    lng: location.coordinates[0],
  };
};

const getGeoJsonFromCoordinates = (coordinates) => {
  if (!coordinates) return null;

  return {
    type: "Point",
    coordinates: [coordinates.lng, coordinates.lat],
  };
};

const normalizeEditLocationValue = (edit = {}) => {
  const venue = edit.venue && typeof edit.venue === "object" ? edit.venue : null;
  const addressFields = edit.addressFields || (typeof edit.address === "object" ? edit.address : null);
  const address = addressFields
    ? formatAddressComponents(addressFields)
    : (typeof edit.address === "string" ? edit.address : venue?.address || "");
  const location = venue?.location || edit.location;

  return {
    address,
    coordinates: getCoordinatesFromGeoJson(location),
    venue,
    addressFields: venue ? null : (addressFields || (address ? parseAddressString(address) : null)),
  };
};

// Support both full day names (sunday) and short day names (sun)
const dayToRRule = {
  sunday: RRule.SU,
  monday: RRule.MO,
  tuesday: RRule.TU,
  wednesday: RRule.WE,
  thursday: RRule.TH,
  friday: RRule.FR,
  saturday: RRule.SA,
  // Short forms for RecurrenceSelector compatibility
  sun: RRule.SU,
  mon: RRule.MO,
  tue: RRule.TU,
  wed: RRule.WE,
  thu: RRule.TH,
  fri: RRule.FR,
  sat: RRule.SA,
};

const WeekDays = ({
  days,
  onChange = () => {
    console.log("changed");
  },
}) => {
  const availableDays = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  return availableDays.map((day, i) => (
    <div
      onClick={() => {
        if (days?.includes(day)) {
          onChange(days.filter((i) => i !== day));
        } else {
          onChange([...days, day]);
        }
      }}
      className={`${days?.includes(day)
        ? "bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
        : "bg-[var(--inyc-input-background-color)] border-[1px] border-[var(--inyc-input-border-color)]"
        } duration-300 cursor-pointer w-[30px] h-[30px] flex items-center justify-center rounded-[50%]`}
    >
      {day[0]}
    </div>
  ));
};

const EditEvent = ({
  handleError = async () => true,
  Ref,
  userLocation,
  edit,
  recommend,
  noPortalTitle = false,
  allowExtraDates = false,
  afterTitle = null,
  flushTop = false,
  onDateChange,
  hideArtistTagging = false,
}) => {
  //#region variables
  const { globalConfig } = useData();
  const categories = globalConfig?.categories || [];
  const [addressErrors, setAddressErrors] = useState([]);
  // const [marker, setMarker] = useState(userLocation);
  const [marker, setMarker] = useState();
  const [category, setCategory] = useState("music");
  const [startDate, setStartDate] = useState(new Date());
  const [startTime, setStartTime] = useState(
    // get the current hour with minutes set to 00
    new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }).split(":")[0] + ":00"
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().getTime() + 60 * 60 * 1000)
  );
  const [endTime, setEndTime] = useState(
    // get the current hour + 1 with minutes set to 00
    new Date(new Date().getTime() + 60 * 60 * 1000).toLocaleTimeString(
      "en-GB",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    ).split(":")[0] + ":00"
  );
  const [postError, setPostError] = useState();
  const [timeErrors, setTimeErrors] = useState();
  // every field error from the last submit (client or server), shown under its field; cleared on edit
  const [fieldErrors, setFieldErrors] = useState({});
  const fieldMsg = (key) => {
    const v = fieldErrors?.[key];
    return typeof v === "string" ? v : v?.error || undefined;
  };
  const FIELD_KEYS_BY_SECTION = [
    [".posting-title-field", ["name"]],
    [".posting-category-field", ["category"]],
    [".posting-location-field", ["location", "address", "venueName", "streetAddress", "unit", "city", "state", "zipCode"]],
    [".posting-images-field", ["images"]],
    [".posting-description-field", ["description"]],
  ];
  const clearFieldErrorsAt = (target) => {
    const hit = FIELD_KEYS_BY_SECTION.find(([sel]) => target?.closest?.(sel));
    if (!hit) return;
    setFieldErrors((prev) => {
      if (!hit[1].some((k) => prev?.[k])) return prev;
      const next = { ...prev };
      hit[1].forEach((k) => delete next[k]);
      return next;
    });
  };
  const [venues, setVenues] = useState();
  const [ticketed, setTicketed] = useState(false);
  const [ticketedSelected, setTicketedSelected] = useState(false); // Visual: whether user has made a choice
  const [tickets, setTickets] = useState([{ name: "", priceType: "priced_at", price: "$", priceMax: "", description: "", link: "" }]);
  const [moreDatesNoticeOpen, setMoreDatesNoticeOpen] = useState(false);
  const [extraDates, setExtraDates] = useState([]);

  // Event type: 'single' | 'repeating' | 'multiple'
  const [eventType, setEventType] = useState('single');
  const [repeatEveryNumber, setRepeatEveryNumber] = useState(1);
  const [repeatEveryPeriod, setRepeatEveryPeriod] = useState("week");
  const [repeatOn, setRepeatOn] = useState([]);
  const [repeatEnds, setRepeatEnds] = useState("on");
  const [repeatEndsOnDate, setRepeatEndsOnDate] = useState(new Date());
  const [repeatEndsAfterNumber, setRepeatEndsAfterNumber] = useState(1);
  const [monthlyType, setMonthlyType] = useState('dayOfMonth');
  const [rruleString, setRruleString] = useState("");
  
  // Derived state for backward compatibility
  const recurring = eventType === 'repeating';
  const [editImages, setEditImages] = useState([]);
  const [linkedArtists, setLinkedArtists] = useState([]);
  
  // Location state for LocationSelector
  const [locationValue, setLocationValue] = useState({
    address: '',
    coordinates: null,
    venue: null,
    addressFields: null
  });

  const mapRef = useRef();
  const rootRef = useRef();
  const venuSearchTimer = useRef();
  const nameRef = useRef();
  const addressHandlerRef = useRef(); // Now points to LocationSelector

  const descriptionRef = useRef();
  const imagesRef = useRef();
  const socialsRef = useRef();
  const tagsRef = useRef();
  const ageRestrictionRef = useRef();
  const payTheArtistRef = useRef();
  const location = useLocation();
  //#endregion

  const focusFirstError = (errors = {}) => {
    const keys = Object.keys(errors || {}).filter((key) => {
      const value = errors[key];
      return value && (typeof value !== "object" || value.error || Object.keys(value).length > 0);
    });
    if (keys.length === 0 || !rootRef.current) return;
    setFieldErrors(Object.fromEntries(keys.map((k) => [k, typeof errors[k] === "string" ? errors[k] : errors[k]?.error || "please check this field"])));

    const selectorByKey = {
      name: ".posting-title-field input",
      category: ".posting-category-field",
      tags: ".posting-tags-field",
      date: ".posting-date-section",
      startDate: 'input[name="startDate"]',
      startTime: 'input[name="startTime"]',
      endTime: 'input[name="endTime"]',
      repeatEndsOnDate: ".posting-date-section",
      location: ".posting-location-field",
      address: ".posting-location-field",
      street: ".posting-location-field",
      city: ".posting-location-field",
      state: ".posting-location-field",
      zip: ".posting-location-field",
      postalCode: ".posting-location-field",
      country: ".posting-location-field",
      images: ".posting-images-field",
      description: ".posting-description-field textarea, .posting-description-field",
      tickets: ".posting-ticket-section",
    };
    const priority = [
      "name",
      "category",
      "tags",
      "date",
      "startDate",
      "startTime",
      "endTime",
      "repeatEndsOnDate",
      "location",
      "address",
      "street",
      "city",
      "state",
      "zip",
      "postalCode",
      "country",
      "images",
      "description",
      "tickets",
    ];
    const firstKey = priority.find((key) => keys.includes(key)) || keys[0];
    const extraDateMatch = firstKey.match(/^extraDates\[(\d+)\]\.(startDate|startTime|endTime)$/);
    const selector = extraDateMatch
      ? `input[name="${extraDateMatch[2]}"]`
      : selectorByKey[firstKey] || selectorByKey[firstKey.split(".")[0]];
    const target = selector ? rootRef.current.querySelector(selector) : null;
    const focusTarget = target?.matches?.("input, textarea, select, button")
      ? target
      : target?.querySelector?.("input, textarea, select, button");

    // mark every failed field red (kit error ring via aria-invalid); cleared once the field is edited
    const fieldsFor = (key) => {
      const m = key.match(/^extraDates\[(\d+)\]\.(startDate|startTime|endTime)$/);
      const sel = m ? `input[name="${m[2]}"]` : selectorByKey[key] || selectorByKey[key.split(".")[0]];
      if (!sel || key === "tickets") return [];
      return [...rootRef.current.querySelectorAll(sel)].flatMap((el) =>
        el.matches('input:not([type="hidden"]), textarea, select, button[role="combobox"]')
          ? [el]
          : [...el.querySelectorAll('input:not([type="hidden"]):not([type="file"]), textarea, button[role="combobox"]')].slice(0, key === "date" ? 3 : 1)
      );
    };
    keys.flatMap(fieldsFor).forEach((field) => {
      field.setAttribute("aria-invalid", "true");
      const events = field.matches('button[role="combobox"]') ? ["click"] : ["input", "change"];
      const clear = () => {
        field.removeAttribute("aria-invalid");
        events.forEach((ev) => field.removeEventListener(ev, clear));
      };
      events.forEach((ev) => field.addEventListener(ev, clear));
    });

    (target || rootRef.current).scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setTimeout(() => focusTarget?.focus?.({ preventScroll: true }), 250);
  };

  useImperativeHandle(Ref, () => ({
    getFormData: async (obj) => {
      // console.log("getting form data");
      const form = await getValue(obj);
      // console.log("got form data ", form);
      return form;
    },
    recommend: () => addressHandlerRef.current?.recommend(),
    error: postError,
    setAddressErrors: (errors) => {
      addressHandlerRef.current?.setErrors?.(errors);
      setAddressErrors(errors);
    },
    focusFirstError,
  }));

  //#region effects
  const fetchVenues = async (latLng) => {
    console.log('fetching venues');
    const veunesData = await getVenues(latLng);
    console.log('veunesData', veunesData);
    setVenues(veunesData);
  };
  useEffect(() => {
    console.log('userLocation', userLocation);
    if (userLocation) {
      fetchVenues(userLocation);
    } else if (edit?.location?.coordinates) {
      fetchVenues({
        lat: edit.location.coordinates[1],
        lng: edit.location.coordinates[0]
      });
    }
  }, [userLocation, edit?.location?.coordinates]);

  useEffect(() => {
    const handleUrlParams = async () => {
      const venue = new URLSearchParams(location.search).get("venue");
      if (venue) {
        setVenueFromId(venue);
      }
    };
    handleUrlParams();
    return () => {
      clearTimeout(venuSearchTimer.current);
    };
  }, []);

  useEffect(() => {
    if (edit) {
      //add images
      if (edit.images && edit.images.length > 0) {
        const processedImages = edit.images.map((img) => {
          if (typeof img === "string") {
            return img.includes("http") ? img : `${backendUrl}/assets/${img}`;
          }
          return img;
        });
        setEditImages(processedImages);
      } else {
        setEditImages([]);
      }
      if (edit.name) {
        nameRef.current.value = edit.name;
      }
      if (edit.category) {
        setCategory(edit.category);
      }

      // date is either under format "start - end" or just "start", convert it to {start,end} with valid dates
      if (edit.date) {
        const { start, end } = edit.date;

        // Get timezone - use edit.timezone[0] if available, otherwise use local timezone
        const eventTimezone = edit.timezone?.[0] || Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Convert to Luxon DateTime
        const startDateTime = DateTime.fromISO(start, {
          zone: eventTimezone,
        }).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone, {
          keepLocalTime: true,
        });
        const endDateTime = DateTime.fromISO(end, {
          zone: eventTimezone,
        }).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone, {
          keepLocalTime: true,
        });

        // Extract and set start date and time
        setStartDate(startDateTime.toJSDate());

        // // Extract and set end date and time
        // setEndDate(endDateTime.toJSDate());

        // Extract and set start time
        setStartTime(startDateTime.toFormat("HH:mm"));

        // Extract and set end time
        setEndTime(endDateTime.toFormat("HH:mm"));
      }
      if (edit.category) {
        setCategory(edit.category);
      }

      if (edit.rrule && edit.rrule.origOptions) {
        setRepeatEveryNumber(edit.rrule.origOptions.interval || 1);
        const period = Object.keys(frequencyMapping).find(
          (key) => frequencyMapping[key] === edit.rrule.origOptions.freq
        );
        setRepeatEveryPeriod(period);
        const byweekday = edit.rrule.origOptions.byweekday;
        if (period === 'month' && byweekday?.length > 0 && byweekday[0]?.n !== undefined) {
          setMonthlyType('dayOfWeek');
          setRepeatOn([]);
        } else {
          setMonthlyType('dayOfMonth');
          setRepeatOn(
            byweekday
              ?.map((day) => {
                return Object.keys(dayToRRule).find(
                  (key) =>
                    dayToRRule[key].weekday === day ||
                    dayToRRule[key].weekday === day?.weekday
                );
              })
              .filter((i) => i !== undefined) || []
          );
        }
        if (edit.rrule.origOptions.until) {
          setRepeatEnds("on");
          // const untilDate = formatRRuleDateToInput(edit.rrule.options.until, edit.timezone);
          const untilDate = formatInputToRRuleDate(
            edit.rrule.origOptions.until,
            edit.timezone
          ).toJSDate();
          // console.log("untilDate ", untilDate);
          setRepeatEndsOnDate(untilDate);
        } else if (edit.rrule.origOptions.count) {
          setRepeatEnds("after");
          setRepeatEndsAfterNumber(edit.rrule.origOptions.count);
        } else {
          // Event has no end date (was "never") - default to "on" with 1 year from start
          setRepeatEnds("on");
          const startDateTime = DateTime.fromISO(edit.date.start, {
            zone: edit.timezone?.[0] || 'local',
          });
          const oneYearFromStart = startDateTime.plus({ years: 1 }).toJSDate();
          setRepeatEndsOnDate(oneYearFromStart);
        }

        setEventType('repeating');
      }
      
      // Handle wizard recurring flag (simpler than full rrule)
      if (edit._wizardRecurring) {
        setEventType('repeating');
        // Set repeat period from wizard
        if (edit._wizardRepeatEveryPeriod) {
          setRepeatEveryPeriod(edit._wizardRepeatEveryPeriod);
        }
        // Set repeat days from wizard
        if (edit._wizardRepeatOn && edit._wizardRepeatOn.length > 0) {
          setRepeatOn(edit._wizardRepeatOn);
        }
        // Set monthly type from wizard
        if (edit._wizardMonthlyType) {
          setMonthlyType(edit._wizardMonthlyType);
        }
      }
      
      // Handle wizard extra dates
      if (edit._wizardExtraDates && edit._wizardExtraDates.length > 0) {
        setExtraDates(edit._wizardExtraDates);
        setEventType('multiple');
      }

      if (edit.venue && typeof edit.venue !== "object") {
        setVenueFromId(edit.venue);
      } else if (edit.venue || edit.address || edit.location || edit.addressFields) {
        const nextLocationValue = normalizeEditLocationValue(edit);
        setLocationValue(nextLocationValue);
        addressHandlerRef.current?.setState({
          address: nextLocationValue.address,
          location: getGeoJsonFromCoordinates(nextLocationValue.coordinates),
          venue: nextLocationValue.venue,
          addressFields: nextLocationValue.addressFields,
          showSelectedCard: true,
        });
      }
      if (edit.ticketed !== undefined) {
        setTicketed(edit.ticketed);
        setTicketedSelected(true); // User has already made a choice when editing
      }
      // Load tickets array (new format) or convert from legacy fields
      if (edit.tickets && edit.tickets.length > 0) {
        setTickets(edit.tickets.map(t => ({
          name: t.name || "",
          priceType: t.priceType || "priced_at",
          price: t.price || "$",
          priceMax: t.priceMax || "",
          description: t.description || "",
          link: t.link || ""
        })));
      } else if (edit.ticket_price || edit.ticket_link) {
        // Legacy format - convert to tickets array
        setTickets([{
          name: "",
          priceType: "priced_at",
          price: edit.ticket_price || "$",
          priceMax: "",
          description: "",
          link: edit.ticket_link || ""
        }]);
      }
      if (edit.socials && edit.socials.length > 0) {
        socialsRef.current.setValue(edit.socials);
      }
      if (edit.website) {
        socialsRef.current.setValue([
          ...(edit.socials || []),
          { platform: "website", link: edit.website },
        ]);
      }

      if (edit.paytheartist_link) {
        payTheArtistRef.current.value = edit.paytheartist_link;
      }

      if (edit.age_restriction) {
        ageRestrictionRef.current.value = edit.age_restriction;
      }
      if (edit.description) {
        descriptionRef.current.value = edit.description;
      }
      if (edit.tags) {
        tagsRef.current.setValue(edit.tags);
      }
      if (edit.linked_artists && Array.isArray(edit.linked_artists)) {
        setLinkedArtists(edit.linked_artists);
      }
      
      // Handle wizard images - set them after a short delay to ensure ImageSlider is mounted
      if (edit._wizardImages && edit._wizardImages.length > 0) {
        setTimeout(() => {
          if (imagesRef.current?.setValue) {
            // Convert wizard images to ImageSlider format
            // Wizard images are { file, base64, original } objects
            const imageItems = edit._wizardImages.map(img => {
              if (img.original) {
                // Already has original URL
                return { file: img.file, original: img.original };
              } else if (img.base64) {
                // Has base64, create blob URL
                return { file: img.file, original: img.base64 };
              } else if (img instanceof File) {
                // Raw File object
                return { file: img, original: URL.createObjectURL(img) };
              }
              return null;
            }).filter(Boolean);
            
            imagesRef.current.setValue(imageItems);
          }
        }, 100);
      }
    }
  }, [edit]);
  //#endregion

  //#region new recurring code

  useEffect(() => {
    // Guard against invalid dates (e.g., while user is typing a partial year)
    if (!startDate || !(startDate instanceof Date) || isNaN(startDate.getTime())) return;
    if (recurring) {
      const rule = getRRule({
        repeatEveryNumber,
        repeatEveryPeriod,
        repeatOn,
        repeatEnds,
        repeatEndsOnDate,
        repeatEndsAfterNumber,
        startDate: startDate,
        startTime: startTime,
        monthlyType,
      });

      setRruleString(rule.text);
    }
  }, [
    recurring,
    repeatEveryNumber,
    repeatEveryPeriod,
    repeatOn,
    repeatEnds,
    repeatEndsOnDate,
    repeatEndsAfterNumber,
    startDate,
    startTime,
    monthlyType,
  ]);

  const formatInputToRRuleDate = (date, timezone) => {
    // Fallback to local timezone if not provided
    const tz = timezone?.[0] || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const datetime =
      date instanceof Date
        ? DateTime.fromJSDate(date, { zone: tz })
        : DateTime.fromISO(date, { zone: tz });
    const formatteddate = datetime
      .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone, {
        keepLocalTime: true,
      })
      .toUTC();

    return formatteddate;
  };

  const getRRuleDays = (days) => {
    // console.log("days", days);
    return days.map((day) => dayToRRule[day?.toLowerCase()]).filter(Boolean);
  };

  const getRRule = ({
    repeatEveryNumber,
    repeatEveryPeriod,
    repeatOn,
    repeatEnds,
    repeatEndsOnDate,
    repeatEndsAfterNumber,
    startDate,
    startTime,
    monthlyType = 'dayOfMonth', // 'dayOfMonth' or 'dayOfWeek'
  }) => {
    try {
      // Guard against invalid dates (e.g., while user is typing a partial year)
      if (!startDate || isNaN(new Date(startDate).getTime())) {
        return { text: "", options: {} };
      }

    const localDateStr = startDate instanceof Date
      ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
      : String(startDate).split('T')[0];
    const startDateTime = new Date(`${localDateStr}T${startTime}`);

      // Check if the constructed datetime is valid
      if (isNaN(startDateTime.getTime())) {
        return { text: "", options: {} };
      }

    let rruleOptions = {
      freq: frequencyMapping[repeatEveryPeriod],
      interval: repeatEveryNumber,
      dtstart: startDateTime,
      tzid: "utc",
    };

    // Handle weekly recurrence with specific days
    if (repeatEveryPeriod === "week" && repeatOn.length > 0) {
      rruleOptions.byweekday = getRRuleDays(repeatOn || []);
    }
    
    // Handle monthly recurrence with "Nth weekday" option (e.g., "2nd Monday")
    if (repeatEveryPeriod === "month" && monthlyType === "dayOfWeek") {
      // Calculate which week of the month (1-5)
      const weekOfMonth = Math.ceil(startDateTime.getDate() / 7);
      // Get day of week (JS: 0=Sunday, RRule: 0=Monday)
      const jsDayOfWeek = startDateTime.getDay();
      // Convert JS day (0=Sun) to RRule weekday constant
      const rruleWeekdays = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
      const weekdayConstant = rruleWeekdays[jsDayOfWeek];
      // Use nth() to specify "Nth occurrence of this weekday"
      rruleOptions.byweekday = [weekdayConstant.nth(weekOfMonth)];
    }
    
    switch (repeatEnds) {
      case "on": {
        const untilDate = repeatEndsOnDate instanceof Date ? repeatEndsOnDate : new Date(repeatEndsOnDate);
        const untilDatePart = DateTime.fromJSDate(untilDate).toFormat("yyyy-MM-dd");
        const untilDT = DateTime.fromISO(`${untilDatePart}T23:59:59`, { zone: "utc" });
        rruleOptions.until = untilDT.toJSDate();
        break;
      }
      case "after":
        rruleOptions.count = parseInt(repeatEndsAfterNumber, 10);
        break;
    }

    const rule = new RRule(rruleOptions);
    // Emit dtstart/until as wall-clock strings matching date.start, so the backend
    // reinterprets both the same way in the event's timezone.
    return {
      text: rule?.toText()?.toLowerCase(),
      options: {
        origOptions: {
          ...rule.origOptions,
          byhour: null,
          byminute: null,
          bysecond: null,
          dtstart: formatDateForInput(startDateTime),
          until:
            repeatEnds === "on"
              ? formatDateForInput(repeatEndsOnDate)
              : undefined,
          tzid: "utc",
        },
      },
    };
    } catch (error) {
      // Return empty result if any error occurs (e.g., invalid date while typing)
      console.warn("getRRule error (likely invalid date while typing):", error.message);
      return { text: "", options: {} };
    }
  };
  //#endregion

  //#region handlers
  // Handle Enter key to dismiss keyboard on native inputs
  const handleNativeInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur the input to dismiss the keyboard
      if (e.target) {
        e.target.blur();
      }
    }
  };

  const handleNativePickerClick = (e) => {
    try {
      e.currentTarget.showPicker?.();
    } catch {
      // picker already open or unsupported
    }
  };

  const setVenueFromId = async (id) => {
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/venues/get/${id}`
      );
      if (data?.data && data?.data?._id) {
        handleVenuPick(data?.data);

        // setAddressFromVenue(data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const venuesToMarkers = (list) => {
    if (!list || list.length === 0) {
      return [];
    }
    let venuMarkers = [];

    const currentVenue = addressHandlerRef?.current?.getVenueId();

    // Iterate over each venue
    list.forEach((venue) => {
      // Get the coordinates from the 'location' field
      const [lng, lat] = venue.location.coordinates;

      // Create a new object with the desired format and push it to the array
      if (venue._id !== currentVenue) {
        venuMarkers.push({
          lng,
          lat,
          icon: barMarker,
          venue: venue,
          props: {
            onVenueSelect: () => {
              console.log('onVenueSelect');
              handleVenuPick(venue);
            },
            onMapClick: () => {
              console.log('onMapClick');
              //set addresshandlers position to current venue latlng 
              addressHandlerRef.current.setState(prev => ({
                ...prev,
                location: {
                  coordinates: [lng, lat]
                }
              }));
            },
            Popup: (props) => {
              const { onClose, onMapClick, onVenueSelect } = props;
              return <Popup
                style={{
                  backgroundColor: "var(--inyc-primary-light)",
                }}
                className="bg-[--inyc-primary] w-full"
                onClose={() => onClose()}
                {...props}
              >
                <div
                  className="bg-[--inyc-primary] w-full"
                  style={{ padding: 'var(--inyc-space-8)', minWidth: '15rem', borderRadius: 'var(--inyc-radius-xs)' }}
                >
                  <div
                    className="mb-kit-8"
                  >
                    <div className="text-[--inyc-primary-text-color-light] text-kit-xs">
                      venue
                    </div>
                    <div className="break-all mb-kit-4 text-[--inyc-primary-text-color] text-kit-md">
                      {venue?.name || 'Venue'}
                    </div>
                    <div className="text-kit-2xs mb-kit-8 font-['Primary-Regular'] text-[--inyc-primary-text-color-light]">
                      {venue?.address}
                    </div>
                  </div>
                  <div
                    className="flex items-center justify-start cursor-pointer px-kit-12 py-kit-8 rounded-kit-xs w-full hover:text-[--inyc-primary-text-color] transition-all duration-300 hover:bg-[--inyc-primary-light] text-[--inyc-primary-text-color] text-kit-base"
                    style={{ width: '100%' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onVenueSelect();
                      onClose();
                    }}
                  >
                    <ImHome
                      className="mr-kit-8"
                      size={11}
                    />
                    <div
                      className="text-kit-base leading-[1.5rem] text-[--inyc-primary-text-color]"
                    >select venue</div>
                  </div>
                  <div
                    className="flex items-center justify-start cursor-pointer px-kit-12 py-kit-8 rounded-kit-xs w-full hover:text-[--inyc-primary-text-color] transition-all duration-300 hover:bg-[--inyc-primary-light] text-[--inyc-primary-text-color] text-kit-base"
                    style={{ width: '100%' }}
                    onClick={(e) => {
                      //should be considered a normal map click
                      e.stopPropagation();
                      e.preventDefault();
                      onMapClick();
                      onClose();
                    }}
                  >
                    <IoLocationSharp
                      className="mr-kit-8"
                      size={12}
                    />
                    <div
                      className="text-kit-base leading-[1.5rem] text-[--inyc-primary-text-color]"
                    >add custom marker</div>
                  </div>
                </div>
              </Popup>
            }
          }
        })
      }
    })

    return venuMarkers;
  };

  const handleVenuPick = async (venue) => {
    // Update location state
    setLocationValue({
      address: venue.address,
      coordinates: venue.location?.coordinates ? {
        lat: venue.location.coordinates[1],
        lng: venue.location.coordinates[0]
      } : null,
      venue: venue,
      addressFields: null
    });
    // Also update via ref for internal AddressHandler
    addressHandlerRef.current?.setState({
      address: venue.address,
      location: venue.location,
      venue: venue
    });
    if (mapRef.current) {
      mapRef.current.flyTo([
        venue.location.coordinates[1],
        venue.location.coordinates[0],
      ]);
    }
  };

  const buildEventDateRange = useCallback(() => {
    if (
      !startDate ||
      !(startDate instanceof Date) ||
      Number.isNaN(startDate.getTime()) ||
      !startTime ||
      !endTime
    ) {
      return null;
    }

    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    if (
      [startHour, startMinute, endHour, endMinute].some((part) => Number.isNaN(part))
    ) {
      return null;
    }

    // Create DateTime objects using Luxon to properly handle timezone
    const startDateTime = DateTime.fromJSDate(startDate)
      .set({
        hour: startHour,
        minute: startMinute
      });
    
    let endDateTime = DateTime.fromJSDate(startDate)
      .set({
        hour: endHour,
        minute: endMinute
      });

    if (endDateTime <= startDateTime) {
      endDateTime = endDateTime.plus({ days: 1 });  // Fix: Assign the result back
    }

    return {
      start: formatDateForInput(startDateTime.toJSDate()),
      end: formatDateForInput(endDateTime.toJSDate()),
    };
  }, [endTime, startDate, startTime]);

  useEffect(() => {
    if (!onDateChange) return;
    const date = buildEventDateRange();
    if (date) onDateChange(date);
  }, [buildEventDateRange, onDateChange]);

  const getValue = async (obj) => {
    setTimeErrors(null);

    const date = buildEventDateRange();
    const startDateTime =
      startDate && startTime
        ? DateTime.fromJSDate(startDate).set({
            hour: Number(startTime.split(":")[0]),
            minute: Number(startTime.split(":")[1])
          })
        : null;

    // collect every client-side problem, then return them all at once
    const clientErrors = {};
    const nextTimeErrors = {};
    const addError = (key, msg, isTime = false) => {
      if (!msg) return;
      clientErrors[key] = msg;
      if (isTime) nextTimeErrors[key] = msg;
    };

    if (!nameRef.current?.value?.trim()) addError("name", "please add a title");
    if (!startDate) addError("startDate", "please pick a date", true);
    if (!startTime) addError("startTime", "please pick a start time", true);
    if (!endTime) addError("endTime", "please pick an end time", true);

    //if a recurring end date is specified then the recurring end date must be after the start date
    if (recurring && repeatEnds === "on" && startDateTime && repeatEndsOnDate < startDateTime.toJSDate()) {
      addError("repeatEndsOnDate", "recurring end date cannot be before the start date", true);
    }
    const { venue, location, address, addressFields } = addressHandlerRef?.current?.getState()

    //if no location coordinates and no venue is selected
    if (!location?.coordinates?.length && !venue) {
      addError("location", "please specify a valid location");
    }

    // Validate address fields if not using a verified venue
    if (!venue && location?.coordinates?.length && addressHandlerRef?.current?.validate) {
      const { isValid, errors: addressErrors } = addressHandlerRef.current.validate();
      if (!isValid) {
        Object.entries(addressErrors).forEach(([field, msg]) => addError(field, msg));
        addError("address", "please fix the address");
      }
    }

    // extra dates need a date, a start and an end time (end before start = past midnight, handled below)
    if (allowExtraDates && extraDates) {
      extraDates.forEach((d, i) => {
        if (!d.startDate) addError(`extraDates[${i}].startDate`, "please specify a valid start date", true);
        if (!d.startTime) addError(`extraDates[${i}].startTime`, "please specify a valid start time", true);
        if (!d.endTime) addError(`extraDates[${i}].endTime`, "please specify a valid end time", true);
      });
    }

    if (Object.keys(clientErrors).length) {
      setTimeErrors(Object.keys(nextTimeErrors).length ? nextTimeErrors : null);
      return { error: errorSummary(clientErrors), errors: clientErrors };
    }

    //add images
    const images = imagesRef.current.getValue();
    const form = new FormData();
    if (images && images.length > 0) {
      for (let img of images) {
        form.append("images", img);
      }
    }

    // get the images that are not files
    const eimages = imagesRef.current.getURLs();

    //build the rrule object
    let rrule = null; // Explicitly null when not recurring (clears old rrule on backend)

    if (recurring) {
      const rule = getRRule({
        repeatEveryNumber,
        repeatEveryPeriod,
        repeatOn,
        repeatEnds,
        repeatEndsOnDate,
        repeatEndsAfterNumber,
        startDate: startDate,
        startTime: startTime,
        monthlyType,
      });

      rrule = rule.options;
    }

    const { _includePreviewDetails, ...eventOverrides } = obj || {};

    //if one of the objects in the social has empty platform or link return error
    const socials = socialsRef.current.getValue();

    console.log('got socials', socials)

    // return;
    const event = {
      _id: edit?._id,
      name: nameRef.current.value,
      category: category,
      images: eimages,
      date: date,
      rrule: rrule,
      description: descriptionRef.current.value,
      // location: marker?.venue ? marker?.venue._id : undefined,
      address: address,
      addressFields: addressFields,
      location: location,
      venue: venue?._id || undefined,
      socials: socials,
      age_restriction: ageRestrictionRef.current.value,
      ticketed: ticketed ? "on" : undefined,
      // Use new tickets array format
      tickets: ticketed ? normalizeTicketsForBackend(tickets) : [],
      paytheartist_link: payTheArtistRef.current?.value,
      tags: tagsRef.current?.getValue(),
      linked_artists: linkedArtists.map(artist => artist._id),
      ...eventOverrides,
    };

    // console.log("event ", event);

    if (allowExtraDates) {
      const _extraDates = extraDates?.map((date) => {
        const localDateStr = `${date.startDate.getFullYear()}-${String(date.startDate.getMonth() + 1).padStart(2, '0')}-${String(date.startDate.getDate()).padStart(2, '0')}`;
        let startDateTime = new Date(`${localDateStr}T${date.startTime}`);
        let endDateTime = new Date(`${localDateStr}T${date.endTime}`);

        // Fix: If end time is before start time, add a day
        if (endDateTime <= startDateTime) {
          endDateTime = new Date(endDateTime.getTime() + 24 * 60 * 60 * 1000); // Add 1 day
        }

        return {
          ...date,
          date: {
            start: formatDateForInput(startDateTime),
            end: formatDateForInput(endDateTime),
          },
        };
      });

      //add _extraDates to the form
      // console.log(_extraDates);
      form.append("_extraDates", JSON.stringify(_extraDates));
    }

    //remove the empty fields from the event object or fields that are ''

    for (let key in event) {
      if (event[key] === undefined || event[key] === "") {
        delete event[key];
      }
    }

    // console.log(event);

    form.append("event", JSON.stringify(event));

    return {
      form,
      ...(_includePreviewDetails
        ? { previewDetails: { venue, linkedArtists } }
        : {}),
    };
  };

  const handleExtraDatesChange = (dateObject, i) => {
    setExtraDates((prev) => {
      const newDates = [...prev];
      // construct rrule string
      const currentDateObject = {
        ...newDates[i],
        ...dateObject,
      };
      // console.log("currentDateObject ", currentDateObject);

      const rule = getRRule({
        repeatEveryNumber: currentDateObject.repeatEveryNumber,
        repeatEveryPeriod: currentDateObject.repeatEveryPeriod,
        repeatOn: currentDateObject.repeatOn,
        repeatEnds: currentDateObject.repeatEnds,
        repeatEndsOnDate: currentDateObject.repeatEndsOnDate,
        repeatEndsAfterNumber: currentDateObject.repeatEndsAfterNumber,
        startDate: currentDateObject.startDate,
        startTime: currentDateObject.startTime,
        monthlyType: currentDateObject.monthlyType || 'dayOfMonth',
      });

      const rruleString = rule.text;

      newDates[i] = {
        ...currentDateObject,
        rruleString,
        rrule: rule.options,
      };
      return newDates;
    });
  };
  //#endregion

  function formatDateForInput(
    date,
    dateOnly = false,
    debug = false,
    noOffset = false
  ) {
    if (!date) return "";
    // debug && alert(`got date ${date}`);
    const local = new Date(date);
    // debug && alert(`local date before setting minutes ${local}`);
    local.setMinutes(date.getMinutes() - date.getTimezoneOffset());

    // debug && alert(`local date ${local}`);

    const dt = DateTime.fromJSDate(date);
    if (!noOffset) {
      dt.toUTC({
        keepLocalTime: true,
      });
    }

    if (dateOnly) {
      // debug && alert(`event date dateOnly ${dt.toFormat("yyyy-MM-dd")}`);
      // Format as YYYY-MM-DD
      return dt.toFormat("yyyy-MM-dd");
      // return local.toJSON().slice(0, 10);
    }
    // debug && alert(`event date ${dt.toFormat("yyyy-MM-dd'T'HH:mm")}`);
    return local.toJSON().slice(0, 16);
  }

  return (
    <div
      ref={rootRef}
      onInputCapture={(e) => clearFieldErrorsAt(e.target)}
      onChangeCapture={(e) => clearFieldErrorsAt(e.target)}
      style={{
        background: "var(--inyc-primary)",
        padding: flushTop ? "0 1rem 1rem" : "1rem",
        // boxShadow: "var(--inyc-shadow-card)",
      }}
    >
      <div
        className="portal-content"
        style={{
          ...styles.SplitContainer,
          ...{ padding: "var(--inyc-space-16)", paddingTop: flushTop ? 0 : "0.25rem" },
        }}
      >
        {noPortalTitle ? <></> : <PortalTitle>{edit?._id ? "edit" : "create"} event</PortalTitle>}
        {afterTitle}
        
        <div
          style={{ ...styles.EventAddOptions, paddingTop: 0 }}
          className="posting-form-editor flex flex-col !gap-kit-32"
        >
          {/* 1. Title */}
          <div className="posting-title-field">
            <Input Ref={nameRef} label="title" placeHolder="input event name" error={fieldMsg("name")} />
          </div>
          
          {/* 2. Category */}
          <div className="posting-category-field">
            <div className="ui-label mb-kit-8">category</div>
            <Dropdown
              aria-label="category"
              value={category}
              onChange={(v) => {
                setCategory(v);
                setFieldErrors((prev) => (prev?.category ? { ...prev, category: undefined } : prev));
              }}
              error={Boolean(fieldMsg("category"))}
              options={(categories || []).map((cat) => ({ value: cat.id, label: cat.id, color: cat.activeColor }))}
              renderValue={(o) => (
                <span className="posting-category-value flex items-center gap-kit-8">
                  {o.label}
                  {o.color && <span className="inline-block h-[8px] w-[8px] rounded-kit-full" style={{ backgroundColor: o.color }} />}
                </span>
              )}
              renderOption={(o) => (
                <span className="flex w-full items-center justify-between">
                  {o.label}
                  {o.color && <span className="ml-kit-8 inline-block h-[8px] w-[8px] rounded-kit-full" style={{ backgroundColor: o.color }} />}
                </span>
              )}
            />
            {fieldMsg("category") && <ErrorMessage>{fieldMsg("category")}</ErrorMessage>}
          </div>

          {/* 3. Genres / Subgenres */}
          <div className="posting-form-field posting-tags-field">
            <div className="ui-label mb-kit-8">genres / subgenres</div>
            <TagInput
              className="posting-tags-control"
              placeHolder="add tags"
              placeHolderNote="(no spaces or special characters)"
              ref={tagsRef}
              maxTags={5}
              counterFormat="compact"
              selectedCategory={category}
            />
            <div className="posting-help inyc-help ml-kit-20">
              add genre tags to help users search your event.
              <br />
              e.g., #jazz, #improv, #offbroadway, #experimental
            </div>
          </div>

          {/* 4. Date Section - visually separated with margins */}
          <div className="posting-date-section">
            {/* Date/Time Inputs */}
            <div className="posting-date-inputs flex items-start flex-col justify-start flex-wrap">
              <div className="posting-date-row flex justify-between gap-kit-10 flex-wrap w-full">
                <div className="posting-date-date max-sm:flex-1">
                  <div className="">
                    <div className="ui-label mb-kit-8">
                      date
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <input
                      aria-invalid={timeErrors?.startDate ? true : undefined}
                        type="date"
                        name="startDate"
                        value={formatDateForInput(startDate, true)}
                        onChange={(e) => {
                          if (e.target.value === "") return;
                          setStartDate(DateTime.fromISO(e.target.value).toJSDate());
                        }}
                        onClick={handleNativePickerClick}
                        onKeyDown={handleNativeInputKeyDown}
                      />
                    </div>
                    {timeErrors?.startDate && (
                      <ErrorMessage>{timeErrors?.startDate}</ErrorMessage>
                    )}
                  </div>
                </div>
                <div className="posting-date-times flex justify-between flex-1 gap-kit-10">
                  <div className="flex-1">
                    <div className="ui-label mb-kit-8">
                      start
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <input
                      aria-invalid={timeErrors?.startTime ? true : undefined}
                        type="time"
                        step={60}
                        name="startTime"
                        value={startTime}
                        onChange={(e) => {
                          if (e.target.value === "") return;
                          setStartTime(e.target.value);
                        }}
                        onClick={handleNativePickerClick}
                        onKeyDown={handleNativeInputKeyDown}
                      />
                    </div>
                    {timeErrors?.startTime && (
                      <ErrorMessage>{timeErrors?.startTime}</ErrorMessage>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="ui-label mb-kit-8">
                      end
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <input
                      aria-invalid={timeErrors?.endTime ? true : undefined}
                        type="time"
                        step={60}
                        name="endTime"
                        value={endTime}
                        onChange={(e) => {
                          if (e.target.value === "") return;
                          setEndTime(e.target.value);
                        }}
                        onClick={handleNativePickerClick}
                        onKeyDown={handleNativeInputKeyDown}
                      />
                    </div>
                    {timeErrors?.endTime && (
                      <ErrorMessage>{timeErrors?.endTime}</ErrorMessage>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recurrence Options - directly below date/time */}
            <RecurrenceSelector
              value={{
                type: eventType,
                repeatEveryNumber,
                repeatEveryPeriod,
                repeatOn,
                monthlyType,
                repeatEndsOn: repeatEndsOnDate ? formatDateForInput(repeatEndsOnDate, true) : '',
                extraDates: extraDates.map(d => ({
                  startDate: d.startDate ? formatDateForInput(d.startDate, true) : '',
                  startTime: d.startTime || '',
                  endTime: d.endTime || ''
                }))
              }}
              onChange={(newValue) => {
                setEventType(newValue.type);
                setRepeatEveryNumber(newValue.repeatEveryNumber || 1);
                setRepeatEveryPeriod(newValue.repeatEveryPeriod || 'week');
                setRepeatOn(newValue.repeatOn || []);
                setMonthlyType(newValue.monthlyType || 'dayOfMonth');
                if (newValue.repeatEndsOn) {
                  setRepeatEndsOnDate(DateTime.fromISO(newValue.repeatEndsOn).toJSDate());
                }
                if (newValue.extraDates) {
                  setExtraDates(newValue.extraDates.map(d => ({
                    startDate: d.startDate ? DateTime.fromISO(d.startDate).toJSDate() : new Date(),
                    startTime: d.startTime || startTime,
                    endTime: d.endTime || endTime,
                    endDate: d.startDate ? DateTime.fromISO(d.startDate).toJSDate() : new Date()
                  })));
                }
              }}
              baseDate={{
                startDate: formatDateForInput(startDate, true),
                startTime: startTime,
                endTime: endTime
              }}
            />

            {/* RRule Summary - show when repeating */}
            {recurring && rruleString && (
              <div className="inyc-help gap-kit-16 w-full flex justify-start items-center mt-kit-8">
                ~ {rruleString} from{" "}
                {new Date(`1970-01-01T${startTime}`).toLocaleTimeString(
                  "en-US",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}{" "}
                to{" "}
                {new Date(`1970-01-01T${endTime}`).toLocaleTimeString(
                  "en-US",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </div>
            )}
          </div>
            
          {/* Legacy extra dates section - hidden when using RecurrenceSelector's 'multiple' type */}
          {false && allowExtraDates && eventType === 'single' && (
              <div className="mt-kit-16">
                {extraDates?.length > 0 &&
                  extraDates.map((date, i) => (
                    <div
                      key={i}
                      className={
                        i !== 0 &&
                        "mt-kit-16 pt-kit-32 border-t-[1px] border-t-[--inyc-input-border-color]/10"
                      }
                    >
                      <div className="relative flex items-start flex-col justify-start flex-wrap gap-kit-32 mb-kit-24">
                        <IconButton
                          label="remove date"
                          variant="danger"
                          className="ml-auto mb-[-2rem]"
                          icon={<IoCloseOutline />}
                          onClick={() => {
                            setExtraDates((prev) => {
                              const newDates = [...prev];
                              newDates.splice(i, 1);
                              return newDates;
                            });
                          }}
                        />
                        <div className="flex justify-between gap-kit-16 flex-wrap w-full">
                          <div className="max-sm:flex-1">
                            <div className="">
                              <div className="ui-label mb-kit-8">
                                date
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <input
                                  type="date"
                                  name="startDate"
                                  value={formatDateForInput(
                                    extraDates[i]?.startDate,
                                    true
                                  )}
                                  onChange={(e) => {
                                    if (e.target.value === "") return;
                                    handleExtraDatesChange(
                                      {
                                        startDate: DateTime.fromISO(e.target.value).toJSDate(),
                                      },
                                      i
                                    );
                                  }}
                                  onKeyDown={handleNativeInputKeyDown}
                                />
                              </div>
                              {timeErrors?.[`extraDates[${i}].startDate`] && (
                                <ErrorMessage>
                                  {timeErrors?.[`extraDates[${i}].startDate`]}
                                </ErrorMessage>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between flex-1 gap-kit-16">
                            <div className="flex-1">
                              <div className="ui-label mb-kit-8">
                                start
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <input
                                  type="time"
                                  step={900}
                                  name="startTime"
                                  value={extraDates[i]?.startTime}
                                  onChange={(e) => {
                                    if (e.target.value === "") return;
                                    handleExtraDatesChange(
                                      {
                                        startTime: e.target.value,
                                      },
                                      i
                                    );
                                  }}
                                  onKeyDown={handleNativeInputKeyDown}
                                />
                              </div>
                              {timeErrors?.[`extraDates[${i}].startTime`] && (
                                <ErrorMessage>
                                  {timeErrors?.[`extraDates[${i}].startTime`]}
                                </ErrorMessage>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="ui-label mb-kit-8">
                                end
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <input
                                  type="time"
                                  step={900}
                                  name="endTime"
                                  value={extraDates[i]?.endTime}
                                  onChange={(e) => {
                                    if (e.target.value === "") return;

                                    handleExtraDatesChange(
                                      {
                                        endTime: e.target.value,
                                      },
                                      i
                                    );
                                  }}
                                  onKeyDown={handleNativeInputKeyDown}
                                />
                              </div>
                              {timeErrors?.[`extraDates[${i}].endTime`] && (
                                <ErrorMessage>
                                  {timeErrors?.[`extraDates[${i}].endTime`]}
                                </ErrorMessage>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                <KitButton
                    variant="secondary"
                    size="sm"
                    icon={<FaPlus />}
                    onClick={() => {
                    setExtraDates((prev) => {
                      // Extra dates are always single events (no recurring option)
                      const lastDate = extraDates[extraDates.length - 1];
                      const newDate = {
                        startDate: lastDate?.startDate || startDate,
                        startTime: lastDate?.startTime || startTime,
                        endTime: lastDate?.endTime || endTime,
                        endDate: lastDate?.endDate || endDate,
                        recurring: false, // Extra dates are always single events
                      };

                      return [...prev, newDate];
                    });
                    setTimeErrors(null);
                  }}
                  >
                    add more dates
                  </KitButton>
              </div>
            )}
            {timeErrors?.general && (
              <div>
                <ErrorMessage>{timeErrors?.general}</ErrorMessage>
              </div>
            )}

          {/* 4. Location */}
          <div
            className="posting-form-field posting-location-field"
            style={{
              marginBottom: "var(--inyc-space-16)",
            }}
          >
            <LocationSelector
              ref={addressHandlerRef}
              value={locationValue}
              onChange={(newLocation) => {
                setLocationValue(newLocation);
                // Fetch nearby venues when location changes
                if (newLocation.coordinates) {
                  fetchVenues({
                    lat: newLocation.coordinates.lat,
                    lng: newLocation.coordinates.lng
                  });
                }
              }}
              userLocation={userLocation}
              mapMarkers={venuesToMarkers(venues)}
              recommendCheckbox={recommend}
            />
            {(fieldMsg("location") || fieldMsg("address")) && (
              <ErrorMessage>{fieldMsg("location") || fieldMsg("address")}</ErrorMessage>
            )}
          </div>

          {/* 5. Images */}
          <div
            className="posting-form-field posting-images-field"
            style={{
              marginBottom: "var(--inyc-space-16)",
            }}
          >
            <div className="ui-label mb-kit-8">
              image
            </div>
            <div className="inyc-help">
              upload up to 10 images of your event
            </div>
            <div className="mt-kit-32">
              <ImageSlider
                className={(hasImages) => `!mx-auto !w-full ${hasImages ? "posting-image-slider" : "!h-[120px] md:!h-[200px] md:!w-[200px]"}`}
                edit
                ref={imagesRef}
                images={editImages}
              />
            </div>
            {fieldMsg("images") && <ErrorMessage>{fieldMsg("images")}</ErrorMessage>}
          </div>

          {/* 6. Socials */}
          <SocialsHandler ref={socialsRef} />

          {/* 7. Description */}
          <Input
            Ref={descriptionRef}
            label="description"
            placeHolder="add an event description, keep it brief, and let users know what to expect. for example 'doors are at 7pm, show starts at 8pm!'"
            multiLine
            className="posting-description-field"
            error={fieldMsg("description")}
            style={{ minHeight: "160px"}}
          />

          {/* 8. Ticketing */}
          <div className="posting-ticket-section">
          <div>
            <div className="ui-label">
              ticketing + rsvp
            </div>
            {/* Ticketing type cards - matching recurrence selector style */}
            <div className="posting-ticket-card-list flex flex-col">
              {/* No tickets option */}
              <OptionCard
                selected={ticketedSelected && !ticketed}
                onClick={() => {
                  setTicketed(false);
                  setTicketedSelected(true);
                }}
                icon={<IoCloseOutline />}
                title="no ticketing"
              />

              {/* Tickets / donation / entry fee option */}
              <OptionCard
                selected={ticketedSelected && ticketed}
                onClick={() => {
                  setTicketed(true);
                  setTicketedSelected(true);
                  if (tickets.length === 0) {
                    setTickets([{ name: "", priceType: "priced_at", price: "$", priceMax: "", description: "", link: "" }]);
                  }
                }}
                icon={<TbTicket size={16} className="rotate-[-90deg]" />}
                title="tickets or rsvp required"
              />
            </div>

            {/* Ticket details - shown when tickets required */}
            {ticketed && (
              <div className="posting-ticket-details flex flex-col">
                {tickets.map((ticket, index) => (
                  <div 
                    key={index} 
                    className="inyc-panel posting-ticket-tier relative flex flex-col"
                  >
                    {/* X button only for multiple tiers */}
                    {tickets.length > 1 && (
                      <IconButton
                        label="remove tier"
                        variant="danger"
                        className="absolute right-kit-12 top-kit-12"
                        icon={<IoCloseOutline />}
                        onClick={() => setTickets(tickets.filter((_, i) => i !== index))}
                      />
                    )}
                    
                    <div className={tickets.length > 1 ? "pr-kit-40" : ""}>
                        <Input
                          label="ticket type"
                          placeHolder="ex. general admission, VIP, balcony, etc."
                          value={ticket.name}
                          onChange={(e) => {
                            const newTickets = [...tickets];
                            newTickets[index].name = e.target.value;
                            setTickets(newTickets);
                          }}
                          className="w-full"
                        />
                    </div>
                    <div>
                      <div className="ui-label">price</div>
                      <div className="flex flex-wrap gap-kit-8">
                        {[
                          { value: 'rsvp', label: 'rsvp' },
                          { value: 'starting_at', label: 'starting at' },
                          { value: 'range', label: 'range' },
                          { value: 'donation', label: 'donation' },
                          { value: 'priced_at', label: 'priced at' },
                          { value: 'door', label: 'door' },
                        ].map((option) => (
                          <Chip
                            key={option.value}
                            selected={ticket.priceType === option.value}
                            onClick={() => {
                              const newTickets = [...tickets];
                              newTickets[index].priceType = option.value;
                              if (option.value !== 'range') newTickets[index].priceMax = "";
                              setTickets(newTickets);
                            }}
                          >
                            {option.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                      <div className="posting-ticket-cost-row flex items-end gap-kit-8">
                          <Input
                            label={ticket.priceType === 'range' ? 'min' : 'cost'}
                            placeHolder="$0"
                            value={ticket.price === "$" ? "" : ticket.price}
                            onChange={(e) => {
                              let value = e.target.value;
                              const allowed = /^\$[0-9]*(\.[0-9]{0,3})?$/;
                              if (value && value[0] !== "$") value = `$${value}`;
                              if (value === "" || value === "$" || allowed.test(value)) {
                                const newTickets = [...tickets];
                                newTickets[index].price = value;
                                setTickets(newTickets);
                              }
                            }}
                            className="!flex-none"
                            style={{ width: '120px' }}
                          />
                          {ticket.priceType === 'range' && (
                            <>
                              <span className="posting-ticket-cost-dash text-[var(--inyc-primary-text-color)] opacity-70">–</span>
                              <Input
                                label="max"
                                placeHolder="$0"
                                value={ticket.priceMax === "$" ? "" : ticket.priceMax}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  const allowed = /^\$[0-9]*(\.[0-9]{0,3})?$/;
                                  if (value && value[0] !== "$") value = `$${value}`;
                                  if (value === "$" || value === "" || allowed.test(value)) {
                                    const newTickets = [...tickets];
                                    newTickets[index].priceMax = value;
                                    setTickets(newTickets);
                                  }
                                }}
                                className="!flex-none"
                                style={{ width: '120px' }}
                              />
                            </>
                          )}
                        </div>
                      <Input
                        label="link"
                        placeHolder="paste the url to rsvp, purchase ticket, venmo account, etc."
                        value={ticket.link}
                        onChange={(e) => {
                          const newTickets = [...tickets];
                          newTickets[index].link = e.target.value;
                          setTickets(newTickets);
                        }}
                        className="w-full"
                      />

                    <div className="flex w-full flex-col">
                      <label className="ui-label">info</label>
                      <textarea
                        placeholder="add any relevant ticketing details or information"
                        value={ticket.description || ""}
                        onChange={(e) => {
                          const newTickets = [...tickets];
                          newTickets[index].description = e.target.value;
                          setTickets(newTickets);
                        }}
                        className="w-full p-kit-12 rounded-kit-sm border-0 outline-none focus:outline-none focus:ring-0 bg-[var(--inyc-input-bg-color)] text-[var(--inyc-primary-text-color)] text-kit-sm resize-none min-h-[60px]"
                        style={{ fontFamily: 'Primary-Regular' }}
                      />
                    </div>
                  </div>
                ))}
                
                <KitButton
                  variant="secondary"
                  size="sm"
                  className="mt-kit-8"
                  icon={<FaPlus />}
                  onClick={() => {
                    setTickets([...tickets, { name: "", priceType: "priced_at", price: "$", priceMax: "", description: "", link: "" }]);
                  }}
                >
                  add another tier
                </KitButton>
              </div>
            )}

          </div>
          </div>

          {/* 9. Linked Artists */}
          {!hideArtistTagging && (
            <ArtistSelector
              selectedArtists={linkedArtists}
              onChange={setLinkedArtists}
              maxArtists={10}
              label="tag artists"
            />
          )}

          {/* 10. Minimum Age */}
          
          <div className="posting-form-field">
          <Input
            style={{}}
            Ref={ageRestrictionRef}
            label="minimum age"
            placeHolder="(optional)"
          />
          </div>
          

      
        </div>
        {/* </div> */}
      </div>
    </div>
  );
};

export default EditEvent;
